import type { FunctionComponent } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { Badge, BadgeSize, BadgeType } from '../../../shared/components/badge/badge';
import { Icon } from '../../../shared/components/icon/icon';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { capitalize, slugify } from '../../../shared/utils/strings';
import type { IDocument } from '../../../verification/queries/verify.graphql';
import {
    IDocumentVerificationStatus,
    IDocumentStatus,
} from '../../../verification/queries/verify.graphql';

import styles from './document.module.scss';

const style = createStyleHelper(styles, 'document');

export interface IDocumentProps {
    className?: string;
    document: IDocument;
}

function badgeType(type: IDocumentVerificationStatus): BadgeType {
    switch (type) {
        case IDocumentVerificationStatus.Rejected:
        case IDocumentVerificationStatus.Expired:
            return BadgeType.DANGER;
        case IDocumentVerificationStatus.Pending:
        case IDocumentVerificationStatus.VerifiedPartial:
            return BadgeType.HELPFUL;
        case IDocumentVerificationStatus.Verified:
            return BadgeType.SUCCESS;
    }
    return BadgeType.INFO;
}

export const Document: FunctionComponent<IDocumentProps> = ({ className, document }) => {
    return (
        <div className={createClassName([className, style()])}>
            <div className={style('file-section')}>
                <div className={style('file-icon')}>
                    <Icon name={'file-alt'} className={style('icon-file')} />
                </div>
                <div className={style('file-info')}>
                    <div className={style('file-side-type')}>
                        <FormattedMessage
                            id={`account.verification.${slugify(
                                document.side ? document.side : '',
                            )}.${slugify(document.type ? document.type : '')}`}
                            defaultMessage={`${document.side} of ${document.type}`}
                        />
                    </div>

                    <div className={style('file-name')}>{document.fileName}</div>

                    <div className={style('file-doc-expiry')}>
                        {document.expiresOn ? (
                            <>
                                <div className={style('file-lable')}>
                                    <FormattedMessage
                                        id={'account.verification.expiration.label'}
                                        defaultMessage={'Expiration'}
                                    />
                                </div>
                                <div className={style('file-value-expiry')}>
                                    <FormattedDate value={document.expiresOn} dateStyle={'short'} />
                                    {document.status === IDocumentStatus.Outdated ||
                                    document.status === IDocumentStatus.Expired ? (
                                        <div className={style('file-expiry-badge')}>
                                            <Badge
                                                type={BadgeType.DANGER}
                                                size={BadgeSize.CAPTION}
                                                pill
                                            >
                                                {document.status}
                                            </Badge>
                                        </div>
                                    ) : null}
                                </div>
                            </>
                        ) : null}
                    </div>
                    <div className={style('file-doc-num')}>
                        {document.documentNumber ? (
                            <>
                                <div className={style('file-lable')}>
                                    <FormattedMessage
                                        id={'account.verification.document-number.label'}
                                        defaultMessage={'Document Number'}
                                    />
                                </div>
                                <div className={style('file-value')}>{document.documentNumber}</div>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className={style('file-statuses')}>
                <div className={style('file-doc-verification-status')}>
                    {document.verificationStatus ? (
                        <div className={style('file-doc-verification-badge')}>
                            <Badge
                                type={badgeType(document.verificationStatus)}
                                size={BadgeSize.BODY}
                                pill
                            >
                                <FormattedMessage
                                    id={`account.verification.status.${document.verificationStatus.toLocaleLowerCase()}`}
                                    defaultMessage={
                                        capitalize(
                                            document.verificationStatus.toLocaleLowerCase(),
                                        ) ?? ''
                                    }
                                />
                            </Badge>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
